import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSalesPointOfSale } from '../../../contexts/SalesPointOfSaleContext';
import { getPointOfSales } from '../../../services/pointOfSalesService';
import {
  getSalesPriceList,
  updateSalesPriceListItem,
  updatePosMinPriceRange,
} from '../../../services/salesService';
import PriceListOffersSection from '../../../components/Ventas/PriceListOffersSection';
import PriceListServicesSection from '../../../components/Ventas/PriceListServicesSection';
import PriceListSaleTypesSection from '../../../components/Ventas/PriceListSaleTypesSection';
import PriceListPosConfigExcelToolbar from '../../../components/Ventas/PriceListPosConfigExcelToolbar';
import PriceListPricesExcelToolbar from '../../../components/Ventas/PriceListPricesExcelToolbar';
import Toast from '../../../components/Common/Toast';
import MutateGate from '../../../components/Common/MutateGate';

const VentasPriceList = () => {
  const location = useLocation();
  const { selectedPointOfSale: globalPos } = useSalesPointOfSale();
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [activePosId, setActivePosId] = useState(globalPos?.id ? String(globalPos.id) : '');
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'prices');
  const [items, setItems] = useState([]);
  const [listSettings, setListSettings] = useState({
    pos_assignment_enabled: true,
    price_ranges_enabled: false,
    offers_enabled: true,
    min_price_range_enabled: false,
    min_price_range_percent: null,
    sale_price_types: [],
  });
  const [minRangePercentDraft, setMinRangePercentDraft] = useState('10');
  const [savingMinRange, setSavingMinRange] = useState(false);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 50, total: 0 });
  const [page, setPage] = useState(1);
  const perPage = 50;
  const [loadingPoints, setLoadingPoints] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const activePos = pointsOfSale.find((p) => String(p.id) === activePosId);
  const showMinRange = listSettings.min_price_range_enabled;
  const showOffers = listSettings.offers_enabled;
  const showPosConfig = listSettings.pos_assignment_enabled;
  const salePriceTypes = listSettings.sale_price_types || [];
  const hasSaleTypes = salePriceTypes.length > 0;

  const tabs = useMemo(() => {
    const list = [
      { id: 'prices', label: 'Precios' },
      { id: 'services', label: 'Servicios' },
    ];
    if (showPosConfig) list.push({ id: 'config', label: 'Configuración POS' });
    list.push({ id: 'sale-types', label: 'Tipos de venta' });
    if (showOffers) list.push({ id: 'offers', label: 'Ofertas' });
    return list;
  }, [showPosConfig, showOffers]);

  const showToast = useCallback((message, type = 'info') => {
    setToast({ isOpen: true, message, type });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingPoints(true);
        const res = await getPointOfSales();
        if (!cancelled && res.success) {
          const list = (res.data || []).filter((p) => p.is_active !== false);
          setPointsOfSale(list);
          setActivePosId((prev) => {
            if (prev) return prev;
            if (globalPos?.id) return String(globalPos.id);
            if (list.length === 1) return String(list[0].id);
            return '';
          });
        }
      } catch {
        if (!cancelled) {
          setToast({ isOpen: true, message: 'Error al cargar puntos de venta', type: 'error' });
        }
      } finally {
        if (!cancelled) setLoadingPoints(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [globalPos?.id]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [activePosId, debouncedSearch, activeTab]);

  const loadList = useCallback(async () => {
    if (!activePosId || activeTab === 'offers' || activeTab === 'services' || activeTab === 'sale-types') {
      if (activeTab === 'offers' || activeTab === 'services' || activeTab === 'sale-types') setLoading(false);
      if (!activePosId) {
        setItems([]);
        setMeta({ current_page: 1, last_page: 1, per_page: perPage, total: 0 });
      }
      return;
    }
    setLoading(true);
    try {
      const res = await getSalesPriceList(
        Number(activePosId),
        debouncedSearch,
        page,
        perPage,
        activeTab,
      );
      if (res.success) {
        setItems(res.data || []);
        setMeta(res.meta || { current_page: page, last_page: 1, per_page: perPage, total: res.data?.length || 0 });
        if (res.settings) {
          setListSettings(res.settings);
          if (res.settings.min_price_range_percent != null) {
            setMinRangePercentDraft(String(res.settings.min_price_range_percent));
          }
        }
      }
    } catch (error) {
      const details = error.response?.data?.errors;
      const extra = details ? Object.values(details).flat().join(' ') : '';
      const message = error.response?.data?.message || 'Error al cargar lista de precios';
      setToast({
        isOpen: true,
        message: extra ? `${message} ${extra}` : message,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [activePosId, debouncedSearch, page, activeTab]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const patchRow = async (productId, payload) => {
    setSavingId(productId);
    try {
      const res = await updateSalesPriceListItem(productId, {
        point_of_sale_id: Number(activePosId),
        ...payload,
      });
      if (res.success) {
        setItems((prev) =>
          prev.map((row) =>
            row.id === productId
              ? {
                  ...row,
                  ...res.data,
                  type_prices: res.data.type_prices
                    ? { ...row.type_prices, ...res.data.type_prices }
                    : row.type_prices,
                }
              : row,
          ),
        );
      }
      return res;
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al guardar',
        type: 'error',
      });
      return null;
    } finally {
      setSavingId(null);
    }
  };

  const handlePriceBlur = async (productId, rawValue, salesPriceTypeId = null) => {
    if (!activePosId) return;
    const trimmed = String(rawValue ?? '').trim();
    const price = trimmed === '' ? null : parseFloat(trimmed);
    if (trimmed !== '' && (Number.isNaN(price) || price < 0)) {
      setToast({ isOpen: true, message: 'Precio inválido', type: 'error' });
      return;
    }
    const payload = { price };
    if (salesPriceTypeId) {
      payload.sales_price_type_id = salesPriceTypeId;
    }
    await patchRow(productId, payload);
  };

  const handleToggleEnabled = async (productId, enabled) => {
    await patchRow(productId, { is_enabled_for_pos: enabled });
  };

  const handleMinRangeToggle = async () => {
    if (!activePosId) return;
    const enabling = !listSettings.min_price_range_enabled;
    if (enabling) {
      const pct = parseFloat(minRangePercentDraft);
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        setToast({ isOpen: true, message: 'Indique un porcentaje entre 0 y 100', type: 'error' });
        return;
      }
    }
    setSavingMinRange(true);
    try {
      const res = await updatePosMinPriceRange({
        point_of_sale_id: Number(activePosId),
        min_price_range_enabled: enabling,
        min_price_range_percent: enabling ? parseFloat(minRangePercentDraft) : null,
      });
      if (res.success) {
        setListSettings((prev) => ({ ...prev, ...res.data }));
        setToast({
          isOpen: true,
          message: res.message,
          type: 'success',
        });
        loadList();
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al guardar rango mínimo',
        type: 'error',
      });
    } finally {
      setSavingMinRange(false);
    }
  };

  const handleApplyMinRangePercent = async () => {
    if (!activePosId || !listSettings.min_price_range_enabled) return;
    const pct = parseFloat(minRangePercentDraft);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      setToast({ isOpen: true, message: 'Indique un porcentaje entre 0 y 100', type: 'error' });
      return;
    }
    setSavingMinRange(true);
    try {
      const res = await updatePosMinPriceRange({
        point_of_sale_id: Number(activePosId),
        min_price_range_enabled: true,
        min_price_range_percent: pct,
      });
      if (res.success) {
        setListSettings((prev) => ({ ...prev, ...res.data }));
        setToast({ isOpen: true, message: res.message, type: 'success' });
        loadList();
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al aplicar porcentaje',
        type: 'error',
      });
    } finally {
      setSavingMinRange(false);
    }
  };

  const formatMoney = (n) =>
    n == null ? '—' : new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(n);

  const calcPriceRangeFromPercent = (price, percent) => {
    const base = parseFloat(price);
    const pct = parseFloat(percent);
    if (!Number.isFinite(base) || base < 0 || !Number.isFinite(pct) || pct < 0) return null;
    return {
      min: Math.round(base * (1 - pct / 100) * 100) / 100,
      max: Math.round(base * (1 + pct / 100) * 100) / 100,
    };
  };

  const renderPriceRangeHint = (price) => {
    if (!showMinRange) return null;
    const range = calcPriceRangeFromPercent(price, listSettings.min_price_range_percent);
    if (!range) return null;
    return (
      <p className="text-[11px] text-gray-500 mt-1.5 leading-tight">
        <span className="text-amber-700">Mín. {formatMoney(range.min)}</span>
        <span className="mx-1">·</span>
        <span className="text-emerald-700">Máx. {formatMoney(range.max)}</span>
      </p>
    );
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">Lista de precios</h1>
          <p className="text-sm text-gray-500 mt-1">
            Cada punto de venta tiene su lista de precios, catálogo habilitado y ofertas promocionales.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="price-list-pos" className="block text-sm font-medium text-gray-700 mb-1">
              Punto de venta
            </label>
            <select
              id="price-list-pos"
              value={activePosId}
              onChange={(e) => setActivePosId(e.target.value)}
              disabled={loadingPoints}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Seleccione un punto de venta…</option>
              {pointsOfSale.map((pos) => (
                <option key={pos.id} value={pos.id}>
                  {pos.code ? `${pos.code} — ` : ''}
                  {pos.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <input
              type="text"
              placeholder="Buscar por SKU o nombre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={!activePosId || activeTab === 'offers'}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-50"
            />
          </div>
        </div>

        {tabs.length > 1 && (
          <div className="flex gap-2 mb-4 border-b border-gray-200">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'border-teal-600 text-teal-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {activeTab === 'offers' && showOffers ? (
          <PriceListOffersSection activePosId={activePosId} onToast={showToast} />
        ) : activeTab === 'services' ? (
          <PriceListServicesSection
            activePosId={activePosId}
            search={debouncedSearch}
            onToast={showToast}
          />
        ) : activeTab === 'sale-types' ? (
          <PriceListSaleTypesSection
            activePosId={activePosId}
            onToast={showToast}
            onTypesChanged={loadList}
          />
        ) : !activePosId ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">
            Elija un punto de venta para gestionar precios y productos.
          </div>
        ) : loading ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">Cargando…</div>
        ) : (
          <>
            {activeTab === 'config' && (
              <PriceListPosConfigExcelToolbar
                activePosId={activePosId}
                activePos={activePos}
                onUpdated={loadList}
                onToast={showToast}
              />
            )}
            {activeTab === 'prices' && (
              <PriceListPricesExcelToolbar
                activePosId={activePosId}
                activePos={activePos}
                onUpdated={loadList}
                onToast={showToast}
              />
            )}
            {activeTab === 'prices' && (
              <p className="text-sm text-gray-500 mb-3">
                Los precios de <strong>productos</strong> en esta pestaña se interpretan como{' '}
                <strong>con IGV incluido</strong> (precio al cliente). En la pestaña <strong>Servicios</strong> puede
                elegir «Incluido IGV» o «Más IGV» por cada servicio.
              </p>
            )}
            {activeTab === 'prices' && (
              <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex flex-wrap items-end gap-4">
                <MutateGate>
                  <button
                    type="button"
                    onClick={handleMinRangeToggle}
                    disabled={savingMinRange}
                    className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                      showMinRange
                        ? 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    } disabled:opacity-50`}
                  >
                    {showMinRange ? 'Rango mínimo habilitado' : 'Habilitar rango mínimo'}
                  </button>
                </MutateGate>
                {(showMinRange || !listSettings.min_price_range_enabled) && (
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <label
                        htmlFor="min-range-percent"
                        className="block text-xs font-medium text-gray-600 mb-1"
                      >
                        Descuento máximo permitido (%)
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          id="min-range-percent"
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={minRangePercentDraft}
                          onChange={(e) => setMinRangePercentDraft(e.target.value)}
                          disabled={savingMinRange}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500"
                        />
                        <span className="text-sm text-gray-500">%</span>
                      </div>
                    </div>
                    {showMinRange && (
                      <MutateGate>
                        <button
                          type="button"
                          onClick={handleApplyMinRangePercent}
                          disabled={savingMinRange}
                          className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50"
                        >
                          Aplicar %
                        </button>
                      </MutateGate>
                    )}
                  </div>
                )}
                {showMinRange && (
                  <p className="text-sm text-gray-500 w-full md:w-auto md:ml-auto">
                    Precio mínimo = precio − {listSettings.min_price_range_percent ?? '—'}% · Precio máximo
                    = precio + {listSettings.min_price_range_percent ?? '—'}%
                  </p>
                )}
              </div>
            )}
            {items.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">
                {activeTab === 'prices'
                  ? 'No hay productos habilitados en este punto de venta. Use Configuración POS para habilitarlos.'
                  : activeTab === 'config'
                  ? 'No hay productos vendibles. Marque productos como vendibles en Productos (ficha o edición masiva).'
                  : 'No hay productos que coincidan con la búsqueda.'}
              </div>
            ) : (
          <>
            <div className="bg-white rounded-lg shadow-md overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unidad</th>
                    {activeTab === 'config' && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Vende en este POS
                      </th>
                    )}
                    {activeTab === 'prices' && (
                      <>
                        {hasSaleTypes ? (
                          salePriceTypes.map((type) => (
                            <th
                              key={type.id}
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              {type.name} (S/)
                              {type.is_default ? (
                                <span className="block text-[10px] font-normal text-teal-600 normal-case">
                                  predeterminado
                                </span>
                              ) : null}
                            </th>
                          ))
                        ) : (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Precio (S/)
                          </th>
                        )}
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm font-mono text-teal-600">{row.sku || '—'}</td>
                      <td className="px-4 py-4 text-sm text-gray-900">{row.name}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {row.unit_abbreviation || row.unit_of_measure || '—'}
                      </td>
                      {activeTab === 'config' && (
                        <td className="px-4 py-4">
                          <MutateGate>
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={row.is_enabled_for_pos === true}
                                disabled={savingId === row.id}
                                onChange={(e) => handleToggleEnabled(row.id, e.target.checked)}
                                className="text-teal-600 rounded"
                              />
                              <span className="text-sm text-gray-600">
                                {row.is_enabled_for_pos ? 'Habilitado' : 'No vende'}
                              </span>
                            </label>
                          </MutateGate>
                        </td>
                      )}
                      {activeTab === 'prices' && (
                        <>
                          {hasSaleTypes ? (
                            salePriceTypes.map((type) => {
                              const typePrice = row.type_prices?.[String(type.id)] ?? row.type_prices?.[type.id];
                              return (
                                <td key={type.id} className="px-4 py-4 align-top">
                                  <MutateGate>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      defaultValue={typePrice ?? ''}
                                      key={`p-${row.id}-${type.id}-${typePrice ?? 'e'}`}
                                      disabled={savingId === row.id}
                                      onBlur={(e) => handlePriceBlur(row.id, e.target.value, type.id)}
                                      className="w-28 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500"
                                      placeholder="0.00"
                                    />
                                  </MutateGate>
                                  {renderPriceRangeHint(typePrice)}
                                </td>
                              );
                            })
                          ) : (
                            <td className="px-4 py-4 align-top">
                              <MutateGate>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  defaultValue={row.price ?? ''}
                                  key={`p-${row.id}-${row.price ?? 'e'}`}
                                  disabled={savingId === row.id}
                                  onBlur={(e) => handlePriceBlur(row.id, e.target.value)}
                                  className="w-28 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-500"
                                  placeholder="0.00"
                                />
                              </MutateGate>
                              {renderPriceRangeHint(row.price)}
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {meta.last_page > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-3 mt-4 px-2">
                <p className="text-sm text-gray-600">
                  Mostrando {(meta.current_page - 1) * meta.per_page + 1}–
                  {Math.min(meta.current_page * meta.per_page, meta.total)} de {meta.total} productos
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={meta.current_page <= 1 || loading}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-gray-600">
                    Página {meta.current_page} de {meta.last_page}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
                    disabled={meta.current_page >= meta.last_page || loading}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </>
            )}
          </>
        )}
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
};

export default VentasPriceList;
